<template>
    <div class="mb-4">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-14">
            <h6 class="text-gray-900 mb-0">Activity</h6>
            <div class="activity-filters d-flex flex-wrap gap-1" role="group" aria-label="Filter activity">
                <button v-for="opt in activityFilterOptions" :key="opt.key" type="button"
                    class="activity-chip" :class="{ 'is-active': activityFilter === opt.key }"
                    @click="activityFilter = opt.key">
                    {{ opt.label }}
                    <span v-if="activityCounts[opt.key]" class="activity-chip-count">{{ activityCounts[opt.key] }}</span>
                </button>
            </div>
        </div>
        <p v-if="!visibleActivities.length" class="fs-12 text-gray-500 mb-0">No activity in this category yet.</p>
        <ul class="activity-list mb-0 list-unstyled position-relative">
            <li v-for="activity in visibleActivities" :key="activityKey(activity)" class="d-flex gap-12 activity-item">

                <!-- Proof activity: comments and decisions on linked proofs -->
                <div v-if="activity.source === 'proof'" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background rounded-circle d-flex align-items-center justify-content-center"
                        :class="proofIconClass(activity)">
                        <svg v-if="activity.proofEventType === 'comment'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <svg v-else-if="activity.proofEventType === 'notApproved'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12" :class="{ 'mb-12 mt-1': activity.text }">
                            <div class="d-flex flex-wrap gap-1 align-items-center">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                                <router-link :to="`/proof-item?id=${activity.proofId}`" class="fs-12 fw-semibold text-ashore-primary mb-0">
                                    {{ activity.proofName }}
                                </router-link>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>
                        <div v-if="activity.text" class="bg-gray-50 text-gray-700 py-2 px-12 fs-12 border-start border-gray-300 comment-text"
                            v-html="activity.text"></div>
                    </div>
                </div>

                <!-- Job Created Activity (eventTypeId === 1) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.Created" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/create-job-icon.svg" alt="create-job-icon" />
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1 align-items-center">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            <template v-if="getJobCreatedSource(activity.eventValue)">
                                <span class="fs-12 text-gray-700 d-block">from Artwork Intake submission by</span>
                                <router-link
                                    :to="`/artwork-intake/submission/${getJobCreatedSource(activity.eventValue).sourceArtIntakeSubmissionId}`"
                                    class="fs-12 fw-semibold text-ashore-primary mb-0"
                                >{{ getJobCreatedSource(activity.eventValue).submitterName }}</router-link>
                            </template>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- Status Change Activity (eventTypeId === 2) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.StatusChanged" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img :src="getJobStatusIcon(JSON.parse(activity.eventValue).statusCategoryId)" width="14"
                            class="d-block" alt="status-icon">
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{
                                JSON.parse(activity.eventValue).statusName }}</div>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- Comment Activity (eventTypeId === 3) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.Commented" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img :src="getUserAvatar(activity.userId) || '/img/avatar.svg'" width="28"
                            class="d-block rounded-circle" alt="user-avatar-icon">
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-12 mt-1">
                            <div class="d-flex flex-wrap gap-1">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>
                        <div class="bg-gray-50 text-gray-700 py-2 px-12 fs-12 border-start border-gray-300 mb-2 comment-text">
                            <template v-if="activeCommentToBeEdited === Number(activity.eventValue)">
                                <redactor 
                                    ref="commentRedactor"
                                    :text="activity.comment.comment" 
                                    :placeholder="'Edit comment...'"
                                    :comment-api-url="commentFileApi"
                                    :cur-users-taggable="curUsersTaggable"
                                    :show-preview="false"
                                    :show-source="true"
                                    :use-payload-auth="true"
                                    @input="updateEditingCommentText"
                                    class="mb-2" />
                                <div class="d-flex gap-2">
                                    <button class="btn btn-success btn-sm fw-normal"
                                        @click.prevent="saveEditComment(activity)">Save</button>
                                    <button class="btn btn-light btn-sm fw-normal bg-transparent border-0"
                                        @click.prevent="cancelEditComment">Cancel</button>
                                </div>
                            </template>
                            <template v-else>
                                <div>
                                    <div 
                                        :data-comment-id="activity.id"
                                        v-html="activity.comment && activity.comment.comment">
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div class="d-flex align-items-center justify-content-between gap-1">
                            <div class="d-flex gap-1 position-relative">
                                <button class="emoji bg-gray-100 rounded-3 border-0 fs-12"
                                    @click.stop="toggleEmojiPicker(activity.id)">
                                    <img src="/img/emoji-picker.svg" alt="emoji-picker-icon" width="12" />
                                </button>
                                <div v-if="activeEmojiPickerActivityId === activity.id">
                                    <Picker :perLine="8" :emojiSize="20" class="position-absolute emoji-picker"
                                        @select="(emoji) => handleEmojiSelect(emoji, activity)" />
                                </div>
                                <div class="d-flex align-items-center flex-wrap gap-1">
                                    <span class="selected-emoji" :class="{ 'me': commentReaction.reactedByCurrentUser }"
                                        @click.prevent="toggleEmojiReaction(activity, commentReaction.reaction, commentReaction.userReactionId)"
                                        v-for="commentReaction in getGroupedCommentReactions(activity)"
                                        :key="commentReaction.reaction">
                                        {{ getEmoji(commentReaction.reaction) }}{{ commentReaction.count }}
                                    </span>
                                </div>
                            </div>

                            <div class="dropdown dropdown-wrapper-js"
                                v-if="currentUser && activity.userId === currentUser.id">
                                <button class="btn p-1 dropdown-toggle-js"
                                    @click.stop="toggleCommentDropdown(activity.id)">
                                    <img src="/img/dots-horizontal.svg" width="14" alt="action-icon">
                                </button>
                                <div class="dropdown-menu comment rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                                    :class="{ 'show': activeCommentDropdownId === activity.id }">
                                    <ul class="list-unstyled mb-0 fs-14 list-group-flush w-280">
                                        <li class="p-0">
                                            <span
                                                class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                                href="#" @click.prevent="toggleEditComment(activity)">
                                                Edit
                                            </span>
                                        </li>
                                        <li class="p-0">
                                            <span
                                                class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                                href="#" @click.prevent="deleteComment(activity)">
                                                Delete
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Assignee Change Activity (eventTypeId === 4 || 9) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.Assigned || activity.eventTypeId === JOB_EVENT_TYPE.Unassigned" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img :src="getUserAvatar(getAssigneeUserId(activity.eventValue))" width="28" class="d-block rounded-circle"
                            alt="user-avatar-icon">
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <template v-if="activity.eventTypeId === JOB_EVENT_TYPE.Unassigned && hasAssigneeInEvent(activity.eventValue)">
                                <span class="fs-12 text-gray-700 d-block">unassigned</span>
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ getAssigneeName(activity.eventValue) }}</div>
                                <span class="fs-12 text-gray-700 d-block">from the job</span>
                            </template>
                            <template v-else>
                                <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.Assigned" class="fs-12 fw-semibold text-gray-900 mb-0">
                                    {{ getAssigneeName(activity.eventValue) }}
                                </div>
                            </template>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- Start Date Change Activity (eventTypeId === 5 || 10) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.StartDateChanged || activity.eventTypeId === JOB_EVENT_TYPE.StartDateRemoved" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/calendar-time.svg" width="14" class="d-block" alt="calendar-time-icon">
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.StartDateChanged" class="fs-12 fw-semibold text-gray-900 mb-0">
                                {{ formatLocalDate(activity.eventValue) }}
                            </div>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- End Date Change Activity (eventTypeId === 6 || 11) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.EndDateChanged || activity.eventTypeId === JOB_EVENT_TYPE.EndDateRemoved" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/calendar-check.svg" width="14" class="d-block" alt="calendar-check-icon">
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.EndDateChanged" class="fs-12 fw-semibold text-gray-900 mb-0">
                                {{ formatLocalDate(activity.eventValue) }}
                            </div>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- Tag Change Activity (eventTypeId === 12) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.TagsUpdated" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/tags.svg" width="14" class="d-block" alt="tags-icon">
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-12 mt-1">
                            <div class="d-flex flex-wrap gap-1">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">
                                    {{ getTagChangeDescription(activity.eventValue) || activity.eventType }}
                                </span>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>

                        <div class="d-flex align-items-center flex-wrap gap-1">
                            <template v-if="getTagChange(activity.eventValue).added.length || getTagChange(activity.eventValue).removed.length">
                                <template v-for="tag in getTagChange(activity.eventValue).added">
                                    <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 tag-added">
                                        {{ getTagDisplayName(tag) }}
                                    </div>
                                </template>
                                <template v-for="tag in getTagChange(activity.eventValue).removed">
                                    <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 tag-removed">
                                        {{ getTagDisplayName(tag) }}
                                    </div>
                                </template>
                            </template>
                            <template v-else>
                                <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12">
                                    {{ activity.eventValue }}
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Priority Change Activity (eventTypeId === 8) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.PriorityChanged" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img :src="getJobPriorityIcon(Number(activity.eventValue))" width="14" class="d-block"
                            alt="priority-icon">
                    </div>
                    <div class="flex-fill d-flex align-items-center justify-content-between flex-wrap gap-12">
                        <div class="d-flex flex-wrap gap-1">
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                            <span class="fs-12 text-gray-700 d-block">{{ activity.eventType }}</span>
                            <div class="fs-12 fw-semibold text-gray-900 mb-0">
                                {{ getJobPriorityLabel(Number(activity.eventValue)) }}
                            </div>
                        </div>
                        <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                    </div>
                </div>

                <!-- Proofs Updated Activity (eventTypeId === 13) -->
                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.ContactsUpdated" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/users.svg" width="14" class="d-block" alt="contacts-icon">
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-12 mt-1">
                            <div class="d-flex flex-wrap gap-1">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">updated the job contacts</span>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>
                        <div class="d-flex align-items-center flex-wrap gap-1">
                            <template v-for="c in getContactChange(activity.eventValue).added">
                                <div :key="`a-${c.id}`" class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 contact-added">
                                    {{ c.name }}
                                </div>
                            </template>
                            <template v-for="c in getContactChange(activity.eventValue).removed">
                                <div :key="`r-${c.id}`" class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 contact-removed">
                                    {{ c.name }}
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.FileAdded || activity.eventTypeId === JOB_EVENT_TYPE.FileRemoved" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/icon-attachment.svg" width="14" class="d-block" alt="file-icon">
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-12 mt-1">
                            <div class="d-flex flex-wrap gap-1">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">
                                    {{ activity.eventTypeId === JOB_EVENT_TYPE.FileAdded ? 'added a file' : 'removed a file' }}
                                </span>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>
                        <div v-if="activity.eventValue" class="d-flex align-items-center flex-wrap gap-1">
                            <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12">
                                {{ activity.eventValue }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="activity.eventTypeId === JOB_EVENT_TYPE.ProofsUpdated" class="d-flex gap-2 flex-fill">
                    <div class="box-28 icon-background bg-gray-50 rounded-circle d-flex align-items-center justify-content-center">
                        <img src="/img/icon-attachment.svg" width="14" class="d-block" alt="proofs-icon">
                    </div>
                    <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mb-12 mt-1">
                            <div class="d-flex flex-wrap gap-1">
                                <div class="fs-12 fw-semibold text-gray-900 mb-0">{{ activity.userName }}</div>
                                <span class="fs-12 text-gray-700 d-block">
                                    {{ getProofChangeDescription(activity.eventValue) || activity.eventType }}
                                </span>
                            </div>
                            <div class="text-gray-400 fs-12">{{ formatDateTime(activity.createdAt) }}</div>
                        </div>

                        <div class="d-flex align-items-center flex-wrap gap-1">
                            <template v-if="getProofChange(activity.eventValue).added.length || getProofChange(activity.eventValue).removed.length">
                                <template v-for="proof in getProofChange(activity.eventValue).added">
                                    <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 proof-added">
                                        {{ getProofDisplayName(proof) }}
                                    </div>
                                </template>
                                <template v-for="proof in getProofChange(activity.eventValue).removed">
                                    <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12 proof-removed">
                                        {{ getProofDisplayName(proof) }}
                                    </div>
                                </template>
                            </template>
                            <template v-else>
                                <div class="text-gray-800 bg-gray-100 rounded-pill tags fs-12">
                                    {{ activity.eventValue }}
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import { Picker } from 'emoji-mart-vue'
import 'emoji-mart-vue/css/emoji-mart.css';
import redactor from '../form/redactor.vue';

import { JOB_STATUS_META, JOB_PRIORITY_META, JOB_EVENT_TYPE, TYPES_ENUM } from '../../utils/constants'
import { formatDateTime, formatLocalDate, createTaggableUsers, addTargetBlankToLinks } from '../../utils/helpers';

const { STRING, OBJECT } = TYPES_ENUM;

export default {
    name: 'JobActivities',
    components: {
        Picker,
        redactor
    },
    props: {
        activities: {
            type: Array,
            default: () => []
        },
        comments: {
            type: Array,
            default: () => []
        },
        accountUsers: {
            type: Array,
            default: () => []
        },
        jobTags: {
            type: Array,
            default: () => []
        },
        currentUser: {
            type: Object,
            default: null
        },
        jobStatuses: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            activeEmojiPickerActivityId: null,
            activeCommentToBeEdited: null,
            editingCommentText: '',
            activeCommentDropdownId: null,
            JOB_STATUS_META,
            JOB_PRIORITY_META,
            JOB_EVENT_TYPE,
            activityFilter: 'all',
            activityFilterOptions: [
                { key: 'all', label: 'All' },
                { key: 'comment', label: 'Comments' },
                { key: 'decision', label: 'Decisions' },
                { key: 'status', label: 'Status moves' },
                { key: 'other', label: 'Other' },
            ],
        };
    },
    methods: {
        createTaggableUsers,
        activityKey(activity) {
            return `${activity.source || 'job'}-${activity.id}`;
        },
        activityCategory(activity) {
            if (activity.category) return activity.category;
            if (activity.eventTypeId === JOB_EVENT_TYPE.StatusChanged) return 'status';
            if (activity.eventTypeId === JOB_EVENT_TYPE.Commented) return 'comment';
            return 'other';
        },
        proofIconClass(activity) {
            if (activity.proofEventType === 'approved' || activity.proofEventType === 'approvedWithChanges') return 'proof-icon-approved';
            if (activity.proofEventType === 'notApproved') return 'proof-icon-rejected';
            return 'proof-icon-comment';
        },
        formatLocalDate,
        formatDateTime,
        getEmoji(code) {
            return String.fromCodePoint(parseInt(code, 16));
        },
        getUserAvatar(userId) {
            const user = this.accountUsers.find(u => u.id === userId);
            return user && user.profileImageUrl ? user.profileImageUrl : '/img/review/avatar.svg';
        },
        getUserName(userId) {
            const user = this.accountUsers.find(u => u.id === userId);
            return user ? user.name : 'Unknown User';
        },
        getAssigneeUserId(eventValue) {
            try {
                if (typeof eventValue === STRING && eventValue.startsWith('{')) {
                    const parsed = JSON.parse(eventValue);
                    return parsed.UserId || parsed.userId;
                }
                return Number(eventValue);
            } catch (e) {
                console.error('Failed to parse assignee eventValue:', eventValue, e);
                return Number(eventValue) || 0;
            }
        },
        getJobCreatedSource(eventValue) {
            if (!eventValue || typeof eventValue !== "string" || !eventValue.startsWith("{")) return null;
            try {
                const parsed = JSON.parse(eventValue);
                if (parsed && parsed.sourceArtIntakeSubmissionId) return parsed;
            } catch (e) { /* fall through */ }
            return null;
        },
        hasAssigneeInEvent(eventValue) {
            if (!eventValue) return false;
            if (typeof eventValue === STRING && eventValue.startsWith('{')) {
                try { return !!JSON.parse(eventValue).UserId; } catch (e) { return false; }
            }
            return Number(eventValue) > 0;
        },
        getAssigneeName(eventValue) {
            try {
                if (typeof eventValue === STRING && eventValue.startsWith('{')) {
                    const parsed = JSON.parse(eventValue);
                    return parsed.Name || parsed.name || 'Unknown User';
                }
                return this.getUserName(Number(eventValue));
            } catch (e) {
                console.error('Failed to parse assignee eventValue:', eventValue, e);
                return this.getUserName(Number(eventValue)) || 'Unknown User';
            }
        },
        getJobStatusIcon(eventData) {
            return JOB_STATUS_META[eventData].icon || '/img/block.svg';
        },
        getJobStatusLabel(statusId) {
            const status = this.jobStatuses.find(s => s.id === statusId);
            return status ? status.name : 'Unknown Status';
        },
        getJobPriorityIcon(priorityId) {
            return JOB_PRIORITY_META[priorityId].icon || '/img/block.svg';
        },
        getJobPriorityLabel(priorityId) {
            return JOB_PRIORITY_META[priorityId].label || 'Unknown Priority';
        },
        getTagChange(value) {
            try {
                const parsed = typeof value === STRING ? JSON.parse(value) : (value || {});
                const added = Array.isArray(parsed.added) ? parsed.added : [];
                const removed = Array.isArray(parsed.removed) ? parsed.removed : [];
                return { added, removed };
            } catch (e) {
                return { added: [], removed: [] };
            }
        },
        getTagDisplayName(tag) {
            try {
                if (tag && typeof tag === OBJECT) {
                    return tag.Name || tag.name || 'Unknown Tag';
                }
                if (typeof tag === STRING) {
                    return tag;
                }
                return 'Unknown Tag';
            } catch (e) {
                console.error('Failed to get tag display name:', tag, e);
                return 'Unknown Tag';
            }
        },
        getTagChangeDescription(value) {
            try {
                const { added, removed } = this.getTagChange(value);
                const hasAdded = added && added.length > 0;
                const hasRemoved = removed && removed.length > 0;
                if (hasAdded && hasRemoved) return 'added and removed the following tags';
                if (hasAdded) return 'added the following tags';
                if (hasRemoved) return 'removed the following tags';
                return '';
            } catch (e) {
                console.error('Failed to get tag change description:', value, e);
                return '';
            }
        },
        getContactChange(value) {
            try {
                const parsed = typeof value === STRING ? JSON.parse(value) : (value || {});
                const norm = (arr) => (Array.isArray(arr) ? arr : []).map((c) => ({
                    id: c.id != null ? c.id : c.Id,
                    name: c.name || c.Name || 'Unknown Contact',
                }));
                return { added: norm(parsed.added), removed: norm(parsed.removed) };
            } catch (e) {
                return { added: [], removed: [] };
            }
        },
        getProofChange(value) {
            try {
                const parsed = typeof value === STRING ? JSON.parse(value) : (value || {});
                const added = Array.isArray(parsed.added) ? parsed.added : [];
                const removed = Array.isArray(parsed.removed) ? parsed.removed : [];
                return { added, removed };
            } catch (e) {
                return { added: [], removed: [] };
            }
        },
        getProofDisplayName(proof) {
            try {
                if (proof && typeof proof === OBJECT) {
                    return proof.Name || proof.name || 'Unknown Proof';
                }
                if (typeof proof === STRING) {
                    return proof;
                }
                return 'Unknown Proof';
            } catch (e) {
                console.error('Failed to get proof display name:', proof, e);
                return 'Unknown Proof';
            }
        },
        getProofChangeDescription(value) {
            try {
                const { added, removed } = this.getProofChange(value);
                const hasAdded = added && added.length > 0;
                const hasRemoved = removed && removed.length > 0;
                if (hasAdded && hasRemoved) return 'added and removed the following proofs';
                if (hasAdded) return 'added the following proofs';
                if (hasRemoved) return 'removed the following proofs';
                return '';
            } catch (e) {
                console.error('Failed to get proof change description:', value, e);
                return '';
            }
        },
        handleEmojiSelect(emoji, activity) {
            if (!activity || !emoji) {
                console.error('Missing activity or emoji');
                return;
            }

            // Comment is now directly included in the activity object
            const selectedComment = activity.comment;
            if (!selectedComment) {
                console.error('Comment not found for activity:', activity);
                return;
            }

            const commentReactionRequest = {
                commentId: selectedComment.id,
                reactionType: emoji.unified
            };

            // Emit event to parent to handle API call
            this.$emit('add-reaction', commentReactionRequest);
            this.activeEmojiPickerActivityId = null;
        },
        removeEmoji(reactionId) {
            this.$emit('remove-reaction', reactionId);
        },
        toggleEmojiReaction(activity, reactionUnified, userReactionId) {
            if (userReactionId) {
                this.removeEmoji(userReactionId);
                return;
            }
            // Comment is now directly included in the activity object
            const selectedComment = activity.comment;
            if (!selectedComment) return;
            const JobCommentReactionRequest = {
                commentId: selectedComment.id,
                reactionType: reactionUnified
            };
            this.$emit('add-reaction', JobCommentReactionRequest);
        },
        toggleEmojiPicker(activityId) {
            this.activeEmojiPickerActivityId = this.activeEmojiPickerActivityId === activityId ? null : activityId;
        },
        toggleCommentDropdown(activityId) {
            this.activeCommentDropdownId = this.activeCommentDropdownId === activityId ? null : activityId;
        },
        startEditComment(activity) {
            this.activeCommentToBeEdited = Number(activity.eventValue);

            // Comment is now directly included in the activity object
            const match = activity.comment;
            this.editingCommentText = match ? (match.comment || '') : '';
        },
        toggleEditComment(activity) {
            this.startEditComment(activity);
        },
        cancelEditComment() {
            this.activeCommentToBeEdited = null;
            this.editingCommentText = '';
        },
        updateEditingCommentText(htmlContent) {
            this.editingCommentText = htmlContent;
        },
        saveEditComment(activity) {
            const id = Number(activity.eventValue);
            if (!id) return;
            
            const processedComment = addTargetBlankToLinks(this.$refs.commentRedactor?.getTextBody?.() || (this.editingCommentText || '').trim());
            if (!processedComment || processedComment === '' || processedComment === '<p></p>' || processedComment === '<p><br></p>') {
                this.$toast('Comment cannot be empty', {
                    title: 'Error',
                    variant: 'danger',
                });
                return;
            }
            
            this.$emit('save-comment', { commentId: id, comment: processedComment });
            this.cancelEditComment();
        },
        deleteComment(activity) {
            const id = Number(activity.eventValue);
            if (!id) return;
            this.$emit('delete-comment', id);
        },
        handleOutsideClick(event) {
            const emojiPickerSelectors = [
                '.emoji-picker',
                '.emo-picker',
                '.emoji-mart',
                '.emo-mart',
                '[data-emoji-picker]',
                '[class*="emoji"]',
                '[class*="emo-mart"]'
            ];

            const isWithinEmojiPicker = emojiPickerSelectors.some(selector =>
                event.target.closest(selector)
            );

            if (isWithinEmojiPicker) {
                return;
            }
            
            this.activeCommentDropdownId = null;
            this.activeEmojiPickerActivityId = null;
        },
        // Methods to handle realtime events from parent
        handleCommentEdit(payload) {
            try {
                // Find the comment activity in the activities list
                const commentActivityIndex = this.localActivities.findIndex(activity =>
                    activity.eventTypeId === JOB_EVENT_TYPE.Commented && Number(activity.eventValue) === payload.commentId
                );

                if (commentActivityIndex !== -1) {
                    const activity = this.localActivities[commentActivityIndex];
                    this.$set(activity.comment, 'comment', payload.comment);
                }
            } catch (e) {
                console.error('Failed to handle comment edit:', e);
            }
        },
        handleCommentDelete(payload) {
            try {
                // Find and remove the comment activity from the activities list
                const commentActivityIndex = this.localActivities.findIndex(activity =>
                    activity.eventTypeId === JOB_EVENT_TYPE.Commented && Number(activity.eventValue) === payload.commentId
                );

                if (commentActivityIndex !== -1) {
                    const newActivities = [...this.localActivities];
                    newActivities.splice(commentActivityIndex, 1);
                    this.localActivities = newActivities;
                }
            } catch (e) {
                console.error('Failed to handle comment delete:', e);
            }
        },
        handleReactionAdded(payload) {
            try {
                const commentId = Number(payload.commentId);
                if (!commentId) return;

                // Find the activity with the matching comment
                const activity = (this.localActivities || []).find(a =>
                    a && a.eventTypeId === JOB_EVENT_TYPE.Commented && a.comment && Number(a.comment.id) === commentId
                );
                if (!activity || !activity.comment) return;

                const comment = activity.comment;
                if (!Array.isArray(comment.reactions)) this.$set(comment, 'reactions', []);

                // Prevent duplicates by id if provided
                const existing = comment.reactions.find(r => r && r.id === payload.reactionId);
                if (existing) return;

                const reactionRecord = {
                    id: payload.reactionId,
                    reaction: payload.reaction,
                    reactedBy: payload.reactedBy,
                    createdAt: payload.createdAt || new Date().toISOString(),
                };
                comment.reactions.push(reactionRecord);
            } catch (e) {
                console.error('Failed to handle reaction add:', e);
            }
        },
        handleReactionRemoved(payload) {
            try {
                const commentId = Number(payload.commentId);
                if (!commentId) return;

                // Find the activity with the matching comment
                const activity = (this.localActivities || []).find(a =>
                    a && a.eventTypeId === JOB_EVENT_TYPE.Commented && a.comment && Number(a.comment.id) === commentId
                );
                if (!activity || !activity.comment) return;

                const comment = activity.comment;
                if (!Array.isArray(comment.reactions)) return;

                // Prefer removal by reactionId; fallback to reaction + reactedBy match
                let index = -1;
                if (payload.reactionId != null) {
                    index = comment.reactions.findIndex(r => r && r.id === payload.reactionId);
                }
                if (index === -1) {
                    index = comment.reactions.findIndex(r => r && r.reaction === payload.reaction && r.reactedBy === payload.reactedBy);
                }
                if (index !== -1) {
                    comment.reactions.splice(index, 1);
                }
            } catch (e) {
                console.error('Failed to handle reaction remove:', e);
            }
        },
        getGroupedCommentReactions(activity) {
            try {
                if (!activity || activity.eventTypeId !== JOB_EVENT_TYPE.Commented) return [];

                // Comment is now directly included in the activity object
                const { comment } = activity;
                if (!comment || !Array.isArray(comment.reactions)) return [];

                // Sort reactions by createdAt ascending before grouping
                const sortedReactions = [...comment.reactions].sort((a, b) => {
                    const timeA = a?.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
                    const timeB = b?.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
                    return timeA - timeB;
                });

                const groupedReactions = [];
                const reactionMap = new Map();
                for (const reactionItem of sortedReactions) {
                    if (!reactionItem?.reaction) continue;
                    if (!reactionMap.has(reactionItem.reaction)) {
                        const entry = { reaction: reactionItem.reaction, count: 0, reactedByCurrentUser: false, userReactionId: null };
                        reactionMap.set(reactionItem.reaction, entry);
                        groupedReactions.push(entry);
                    }
                    const entry = reactionMap.get(reactionItem.reaction);
                    entry.count += 1;
                    if (this.currentUser && reactionItem.reactedBy === this.currentUser.id) {
                        entry.reactedByCurrentUser = true;
                        entry.userReactionId = reactionItem.id;
                    }
                };
                return groupedReactions;
            } catch (e) {
                console.error('Failed to get selected comments:', e);
                return [];
            }
        }
    },
    computed: {
        localActivities: {
            get() {
                return this.activities || [];
            },
            set(newActivities) {
                this.$parent.activities = newActivities;
            }
        },
        visibleActivities() {
            if (this.activityFilter === 'all') return this.localActivities;
            return this.localActivities.filter(a => this.activityCategory(a) === this.activityFilter);
        },
        activityCounts() {
            const counts = { comment: 0, decision: 0, status: 0, other: 0 };
            for (const a of this.localActivities) counts[this.activityCategory(a)]++;
            return counts;
        },
        curUsersTaggable: function () {
            return createTaggableUsers(this.currentUser, this.accountUsers);
        },
        commentFileApi: function () {
            const jobId = this.$parent?.jobId || this.jobId;
            const authToken = this.$A.Auth.apiToken.token;
            if (this.$A.Auth && jobId) {
                let apiUrl= `/jobs/${jobId}/files?access_token=${authToken}`;
                return apiUrl;
            }
            return "";
        },
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    }
};
</script>

<style scoped>
::v-deep [data-command="loom.toggle"] {
  display: none !important;
}

.w-280 {
    width: 280px;
}

.emoji {
    padding-left: 7px !important;
    padding-right: 7px !important;
    padding-top: 7px !important;
    padding-bottom: 7px !important;
    font-family: none;
    line-height: 0px;
    height: fit-content;
}

.emoji-picker {
    top: 0;
    bottom: auto;
    margin-top: 36px;
    z-index: 5;
}

.selected-emoji {
    cursor: pointer;
    padding: 3px 8px;
    background-color: rgb(63, 118, 255, 0.1);
    border-radius: 6px;
    margin-right: 8px;
}

.selected-emoji.me {
    background-color: lightblue;
}

.user-profile-image {
    border-radius: 50%;
    height: 28px;
    width: 28px;
    background-size: cover;
    vertical-align: middle;
    background-color: #444444;
    display: inline-block;
}

.tag-added {
    background-color: rgba(22, 163, 74, 0.08) !important;
}

.tag-removed {
    background-color: rgba(220, 38, 38, 0.08) !important;
}

.proof-added {
    background-color: rgba(22, 163, 74, 0.08) !important;
}

.proof-removed {
    background-color: rgba(220, 38, 38, 0.08) !important;
}

.contact-added {
    background-color: rgba(22, 163, 74, 0.08) !important;
}

.contact-removed {
    background-color: rgba(220, 38, 38, 0.08) !important;
}

.activity-list {
    position: relative;
}

.activity-item {
    position: relative;
}

.activity-item .box-28 {
    position: relative;
    z-index: 2;
}

.icon-background {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    min-height: 28px !important;
    flex-shrink: 0;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.icon-background img {
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain;
}

.activity-item:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 14px;
    top: 28px;
    width: 1px;
    background-color: #F4F4F4;
    z-index: 0;
    height: calc(100% - 14px);
}

/* Simple link styling to make them clearly visible */
::v-deep .comment-text:not(:has(.rx-editor-container)) a {
    color: #0066cc !important;
    text-decoration: underline !important;
    font-weight: 600 !important;
}

::v-deep.comment-text:not(:has(.rx-editor-container)) a:hover {
    color: #004499 !important;
}

::v-deep.comment-text:not(:has(.rx-editor-container)) p {
    margin-bottom: 0px !important;
}

@media (max-width: 992px) {
    .dropdown-menu.comment {
        box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
        box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12) !important;
        box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08) !important;
    }
}

@media (max-width: 537px) {
    .comment-text {
        width: 280px;
    }
}

.comment-text {
    word-break: break-word !important;
}

::v-deep .comment-text figure {
    margin: 0 !important;
}

::v-deep .comment-text figure img {
    max-width: 100% !important;
    height: auto !important;
    width: auto !important;
    display: block !important;
}

/* Simple see more/see less styling */
.comment-text .btn-link {
    color: #444444 !important;
    padding: 0 !important;
    margin-left: 4px !important;
    border: none !important;
    background: none !important;
}


.activity-chip {
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #4b5563;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1;
    padding: 5px 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
}
.activity-chip:hover { background: #f9fafb; }
.activity-chip.is-active {
    background: #111827;
    border-color: #111827;
    color: #fff;
}
.activity-chip-count {
    font-size: 11px;
    opacity: 0.75;
}
.proof-icon-comment { background: #e6f4ee; color: #0b6b43; }
.proof-icon-approved { background: #e6f4ee; color: #019256; }
.proof-icon-rejected { background: #fdeaec; color: #eb4654; }
</style>
